import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { runScheduledBackup } from './utils/backup';
import Header from './components/Header';
import LeftSidebar from './components/LeftSidebar';
import FilterBar from './components/FilterBar';
import LeadsTable from './components/LeadsTable';
import LeadModal from './components/LeadModal';
import FollowUpTracker from './components/FollowUpTracker';
import SendEmails from './components/SendEmails';
import NewVersionBanner from './components/NewVersionBanner';
import EmailLog from './components/EmailLog';
import Analytics from './components/Analytics';
import LeadGenAgent from './components/LeadGenAgent';
import DailyActivityTracker from './components/DailyActivityTracker';
import Highlights from './components/Highlights';
import PostSales from './components/PostSales';
import PotentialClients from './components/PotentialClients';
import PipelineClients from './components/PipelineClients';
import { matchesLeadSearch, distinctValues } from './utils/leadSearch';
import Meetings from './components/Meetings';
import OnboardedClients from './components/OnboardedClients';
import StagingLeads from './components/StagingLeads';
import ChangeLogs from './components/ChangeLogs';
import UserManagement from './components/UserManagement';
import EmailTemplates from './components/EmailTemplates';
import AiAssistant from './components/AiAssistant';
import VirtualSalesAgent from './components/VirtualSalesAgent';
import SuiteAuthPage from './components/SuiteAuthPage';
import ChangePasswordScreen from './components/ChangePasswordScreen';
import { leadsAPI, dashboardAPI } from './services/api';

// A 401/403 means "sign in" or "set a new password" - the app already shows the
// right screen for that. An "is the backend running?" pop-up on top of it was
// wrong, and after the 15 Sep 2026 sign-out everyone would have seen it.
const isSignInProblem = (error) => [401, 403].includes(error?.response?.status);

// Debounce a fast-changing value so heavy effects (like a 1500-row leads
// fetch) don't run on every keystroke. The search input still feels instant
// — only the downstream network call is throttled to one request per ~300ms
// of idle.
function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Main App Content Component (only shown when logged in)
const AppContent = () => {
  const { isAdmin, user, logout } = useAuth();
  // Load active tab from localStorage, default to 'pre sales'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('salesTrackerActiveTab');
    return savedTab || 'pre sales';
  });
  
  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('salesTrackerActiveTab', activeTab);
  }, [activeTab]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Independent date filters per tab
  const [tabFilters, setTabFilters] = useState(() => {
    const saved = localStorage.getItem('salesTrackerTabFilters');
    return saved ? JSON.parse(saved) : {
      'pre sales': { selectedDate: '', dateFrom: '', dateTo: '' },
      'post sales': { selectedDate: '', dateFrom: '', dateTo: '' },
      'analytics': { selectedDate: '', dateFrom: '', dateTo: '' },
      'potential clients': { selectedDate: '', dateFrom: '', dateTo: '' },
      'onboarded clients': { selectedDate: '', dateFrom: '', dateTo: '' },
      'follow ups': { selectedDate: '', dateFrom: '', dateTo: '' },
      'send emails': { selectedDate: '', dateFrom: '', dateTo: '' },
      'templates': { selectedDate: '', dateFrom: '', dateTo: '' },
      'ai assistant': { selectedDate: '', dateFrom: '', dateTo: '' },
      'activity': { selectedDate: '', dateFrom: '', dateTo: '' },
      'highlights': { selectedDate: '', dateFrom: '', dateTo: '' },
      'change logs': { selectedDate: '', dateFrom: '', dateTo: '' }
    };
  });
  
  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('salesTrackerTabFilters', JSON.stringify(tabFilters));
  }, [tabFilters]);
  
  // Get current tab's filters
  const getCurrentTabFilters = () => tabFilters[activeTab] || { selectedDate: '', dateFrom: '', dateTo: '' };
  const currentFilters = getCurrentTabFilters();
  const selectedDate = currentFilters.selectedDate;
  const dateFrom = currentFilters.dateFrom;
  const dateTo = currentFilters.dateTo;
  
  // Helper to update current tab's filters. Pre Sales and Post Sales share the same filters so counts match.
  const updateCurrentTabFilters = (updates) => {
    setTabFilters(prev => {
      const next = { ...prev, [activeTab]: { ...prev[activeTab], ...updates } };
      if (activeTab === 'pre sales' || activeTab === 'post sales') {
        const shared = { ...next[activeTab] };
        next['pre sales'] = { ...prev['pre sales'], ...shared };
        next['post sales'] = { ...prev['post sales'], ...shared };
      }
      return next;
    });
  };
  
  const setSelectedDate = (value) => updateCurrentTabFilters({ selectedDate: value });
  const setDateFrom = (value) => updateCurrentTabFilters({ dateFrom: value });
  const setDateTo = (value) => updateCurrentTabFilters({ dateTo: value });
  
  const [selectedBDE, setSelectedBDE] = useState('All');
  const [selectedStage, setSelectedStage] = useState('All');
  const [selectedPriority, setSelectedPriority] = useState('All');
  const [selectedInterest, setSelectedInterest] = useState('All'); // Pre-Sales only: All | Interested | Not Interested
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [selectedLeadSource, setSelectedLeadSource] = useState('All');
  const [leadSortOrder, setLeadSortOrder] = useState('desc'); // 'desc' = recent first
  const [searchTerm, setSearchTerm] = useState('');
  // The fetchLeads cache + useEffect deps consume the debounced value, so
  // typing "Salt River" fires ONE network request after 300ms of idle, not
  // nine on every keystroke.
  const debouncedSearchTerm = useDebounced(searchTerm, 300);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [returnToPostSalesLead, setReturnToPostSalesLead] = useState(null); // when opening pre-sales from post-sales form, remember to return here on Back
  const [postSalesReopenLead, setPostSalesReopenLead] = useState(null); // lead to reopen in post-sales form after Back from pre-sales
  const [followUps, setFollowUps] = useState([]); // Always initialize as empty array
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode] = useState(false);
  const [extensionPrefill, setExtensionPrefill] = useState(null);

  // Disaster-recovery backup. The first time an admin opens the app in a new
  // 4-month cycle (Jan-Apr / May-Aug / Sep-Dec), every dataset is pulled down
  // as ONE Excel workbook — a copy of the records that survives the app or the
  // database being unavailable. It is deliberately fire-and-forget: a failure
  // is logged and the cycle is left unrecorded, so the next login tries again.
  // Admins can also trigger it by hand from the user panel.
  const backupAttemptedRef = useRef(false);
  useEffect(() => {
    if (!user || !isAdmin() || backupAttemptedRef.current) return;
    backupAttemptedRef.current = true;
    // Let the dashboard paint and its own fetches settle before adding five
    // more requests and a file write.
    const timer = setTimeout(() => {
      runScheduledBackup({ isAdmin: true })
        .then((r) => { if (r.ran) console.log('[backup] cycle backup saved:', r.fileName, r.counts); })
        .catch((e) => console.warn('[backup] cycle backup skipped:', e.message));
    }, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Track what filter snapshot was used for the last successful fetch (avoid redundant API calls)
  const lastLeadsFetchKey = React.useRef('');
  const lastStatsFetchKey = React.useRef('');
  const followUpsLoadedRef = React.useRef(false);

  // When opened from AGS Sales Hub extension (URL params), open Add Lead modal with prefilled data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') !== 'extension') return;
    const company_name = params.get('company_name');
    const company_url = params.get('company_url');
    const linkedin_url = params.get('linkedin_url');
    const city = params.get('city');
    const state = params.get('state');
    const country = params.get('country');
    const core_business = params.get('core_business');
    const company_overview = params.get('company_overview');
    if (!company_name && !linkedin_url && !company_url) return;
    setExtensionPrefill({
      company_name: company_name || '',
      company_url: company_url || '',
      linkedin_url: linkedin_url || '',
      city: city || '',
      state: state || '',
      country: country || '',
      core_business: core_business || '',
      company_overview: company_overview || ''
    });
    setActiveTab('pre sales');
    setEditingLead(null);
    setShowModal(true);
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
  }, []);

  // Global Ctrl+S / Cmd+S: prevent browser save and dispatch so current page can handle save
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('app-save'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Fetch dashboard stats (with dedup - skip if same filters)
  const fetchStats = useCallback(async (force = false) => {
    const currentFilters = getCurrentTabFilters();
    const key = JSON.stringify({ bde: selectedBDE, ...currentFilters });
    if (!force && key === lastStatsFetchKey.current && stats) return;
    try {
      setLoading(true);
      const params = { bde: selectedBDE };

      // Use date range if available, otherwise use single date
      if (currentFilters.dateFrom && currentFilters.dateTo) {
        params.date_from = currentFilters.dateFrom;
        params.date_to = currentFilters.dateTo;
      } else if (currentFilters.dateFrom) {
        params.date_from = currentFilters.dateFrom;
      } else if (currentFilters.dateTo) {
        params.date_to = currentFilters.dateTo;
      } else if (currentFilters.selectedDate) {
        params.date = currentFilters.selectedDate;
      }

      const response = await dashboardAPI.getStats(params);
      // Handle both response.data (new format) and direct response (old format)
      setStats(response.data || response);
      lastStatsFetchKey.current = key;
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (!isSignInProblem(error)) alert('Error loading dashboard. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  }, [selectedBDE, activeTab, tabFilters]);

  // Fetch ALL leads exactly once per session, then filter client-side. The
  // user explicitly asked: "don't keep loading every time, only refresh if
  // something changed". So filter inputs (BDE / stage / priority / search /
  // date) no longer hit the backend — they drive an in-memory useMemo.
  // Re-fetch fires only on:
  //   • `force=true` (post-write code paths already pass this)
  //   • the very first load (leads.length === 0)
  //   • the cache being empty (e.g. logout/login)
  const fetchLeads = useCallback(async (force = false) => {
    if (!force && leads.length > 0 && lastLeadsFetchKey.current) return;
    try {
      // Background revalidation: keep the existing rows visible while we
      // re-fetch. Only show the full-page spinner on a TRULY empty cache.
      const isInitial = leads.length === 0;
      if (isInitial) setLoading(true);
      const response = await leadsAPI.getAll({}); // no filter params — client-side now
      const leadsData = response.data || [];
      setLeads(leadsData);
      lastLeadsFetchKey.current = 'loaded';
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (leads.length === 0) {
        if (!isSignInProblem(error)) alert('Error loading leads. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  }, [leads.length]);

  // Client-side filter applied to the cached `leads` array. Re-computes on
  // any filter input change but never triggers a backend round-trip —
  // that's what makes the Pre-Sales table feel instant.
  const filteredLeads = useMemo(() => {
    if (!Array.isArray(leads) || leads.length === 0) return leads;
    const currentFilters = getCurrentTabFilters();
    const q = (debouncedSearchTerm || '').trim().toLowerCase();
    const bde = (selectedBDE || 'All').trim();
    const stage = (selectedStage || 'All').trim();
    const priority = (selectedPriority || 'All').trim();
    const interest = (selectedInterest || 'All').trim();
    const country = (selectedCountry || 'All').trim();
    const leadSource = (selectedLeadSource || 'All').trim();
    const dateFrom = currentFilters.dateFrom || null;
    const dateTo = currentFilters.dateTo || null;
    const exactDate = currentFilters.selectedDate || null;
    return leads.filter(l => {
      if (bde !== 'All' && (l.bde_name || '') !== bde) return false;
      if (stage !== 'All' && (l.stage || '') !== stage) return false;
      if (priority !== 'All' && (l.priority || '') !== priority) return false;
      // Interest filter (Pre-Sales): reads the lead-level interest_status set by
      // the toggle in the lead form ('Interested' | 'Not Interested' | '').
      if (interest !== 'All' && (l.interest_status || '') !== interest) return false;
      if (exactDate && l.lead_date !== exactDate) return false;
      if (dateFrom && (!l.lead_date || l.lead_date < dateFrom)) return false;
      if (dateTo && (!l.lead_date || l.lead_date > dateTo)) return false;
      if (country !== 'All' && (l.country || '').trim().toLowerCase() !== country.toLowerCase()) return false;
      if (leadSource !== 'All' && (l.lead_source || '') !== leadSource) return false;
      // Full-record search: company, every POC name/email/phone, BDA, location,
      // website, overview... "solar india" requires both words, "x" -y excludes.
      if (q && !matchesLeadSearch(l, q)) return false;
      return true;
    });
  }, [leads, selectedBDE, selectedStage, selectedPriority, selectedInterest, selectedCountry, selectedLeadSource, debouncedSearchTerm, tabFilters, activeTab, getCurrentTabFilters]);

  // Filter dropdown options come from the data itself, so a new country or
  // lead source shows up the day the first lead uses it.
  const countryOptions = useMemo(() => distinctValues(leads, 'country'), [leads]);
  const leadSourceOptions = useMemo(() => distinctValues(leads, 'lead_source'), [leads]);

  // Fetch follow-ups - don't pass date or BDE filters, fetch all and filter on frontend
  // `silent` skips the global loading flag so in-place refreshes (e.g., after pressing Done) don't
  // replace the follow-up list with a spinner.
  const fetchFollowUps = useCallback(async (force = false, silent = false) => {
    if (!force && followUpsLoadedRef.current && followUps.length > 0) return;
    try {
      // Only show the global spinner on the first cold load. Once we have data in state,
      // re-fetches should keep the existing list on screen and swap it in when ready —
      // avoids the full-page "Loading..." flash every time the tab is opened.
      const hasData = followUps.length > 0;
      if (!silent && !hasData) setLoading(true);
      const response = await dashboardAPI.getFollowUpTracker(force ? { force: true } : {});
      const followUpsData = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      setFollowUps(followUpsData);
      followUpsLoadedRef.current = true;
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [followUps.length]);

  // Expose follow-ups refresh function globally
  useEffect(() => {
    window.followUpsRefresh = () => fetchFollowUps(true);
    return () => {
      delete window.followUpsRefresh;
    };
  }, [fetchFollowUps]);

  // Create or update lead
  const handleSaveLead = async (formData) => {
    try {
      setLoading(true);
      console.log('App: Saving lead:', { editingLead, formData, user: user });
      if (editingLead) {
        console.log('App: Updating lead with ID:', editingLead.id);
        const response = await leadsAPI.update(editingLead.id, formData);
        console.log('App: Update response:', response);
        if (response && response.contact_book_saved === false) {
          alert('Lead saved, but Contact book (emails) could not be saved. Run the database migration: backend/migrations/add-contact-book.sql');
        }
        // Stay on the lead page after save (per product request) — refresh
        // editingLead with the committed data so the modal keeps showing
        // server-of-record values instead of stale form state.
        const fresh = response?.data || response;
        if (fresh && fresh.id) setEditingLead(fresh);
      } else {
        console.log('App: Creating new lead');
        await leadsAPI.create(formData);
        // Lead created silently — close modal on create, keep open on edit.
        setShowModal(false);
        setEditingLead(null);
      }
      // Refresh all data in parallel (no delay needed - backend already committed)
      Promise.allSettled([
        fetchLeads(true),
        fetchStats(true),
        fetchFollowUps(true),
        window.postSalesRefresh?.(),
        window.potentialClientsRefresh?.(),
        window.activityRefresh?.(),
        window.highlightsRefresh?.(),
      ]);
    } catch (error) {
      console.error('Error saving lead:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      
      // Handle "new location of an existing company" — ask the user to confirm,
      // then re-submit with confirm_new_location:true so the backend creates it.
      if (error.response?.data?.requiresLocationConfirm) {
        const data = error.response.data;
        const locs = Array.isArray(data.existingLocations) && data.existingLocations.length
          ? `\n\nExisting location(s): ${data.existingLocations.join('; ')}`
          : '';
        const proceed = window.confirm(`${data.message}${locs}`);
        if (!proceed) {
          return; // Keep the modal open; user cancelled.
        }
        try {
          await leadsAPI.create({ ...formData, confirm_new_location: true });
          setShowModal(false);
          setEditingLead(null);
          Promise.allSettled([
            fetchLeads(true),
            fetchStats(true),
            fetchFollowUps(true),
            window.postSalesRefresh?.(),
            window.potentialClientsRefresh?.(),
            window.activityRefresh?.(),
            window.highlightsRefresh?.(),
          ]);
        } catch (retryErr) {
          const retryMsg = retryErr.response?.data?.message || retryErr.response?.data?.error || retryErr.message;
          alert('Error saving lead: ' + retryMsg);
        }
        return;
      }

      // Handle duplicate POC (email or phone) error
      if (error.response?.data?.error === 'Duplicate POC detected') {
        const errors = error.response?.data?.errors || [];
        const message = error.response?.data?.message || errors.join(' and ');
        alert(`❌ Cannot save lead!\n\n${message}\n\nPlease use a different email or phone number.`);
        return; // Don't close modal, let user fix the issue
      }
      
      // Handle duplicate lead error
      if (error.response?.data?.error === 'Duplicate lead detected') {
        const existingLead = error.response?.data?.existingLead;
        const message = error.response?.data?.message;
        alert(`${message}\n\nExisting lead details:\nCompany: ${existingLead?.company_name}\nDate: ${existingLead?.lead_date}\nStatus: ${existingLead?.stage}\nBDE: ${existingLead?.bde_name}`);
        return; // Don't close modal, let user fix the issue
      }
      
      // Other errors
      alert('Error saving lead: ' + errorMessage + (error.response?.status ? ` (Status: ${error.response.status})` : ''));
    } finally {
      setLoading(false);
    }
  };

  // Delete lead
  const handleDeleteLead = async (id) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        setLoading(true);
        await leadsAPI.delete(id);
        alert('Lead deleted successfully!');
        // Refresh all data in parallel
        Promise.allSettled([
          fetchLeads(true),
          fetchStats(true),
          fetchFollowUps(true),
          window.postSalesRefresh?.(),
          window.potentialClientsRefresh?.(),
          window.activityRefresh?.(),
          window.highlightsRefresh?.(),
        ]);
      } catch (error) {
        console.error('Error deleting lead:', error);
        alert('Error deleting lead');
      } finally {
        setLoading(false);
      }
    }
  };

  // Convert to potential client
  const handleConvertToPotentialClient = async (lead_id) => {
    try {
      setLoading(true);
      const result = await dashboardAPI.convertToPotentialClient(lead_id, user?.id);
      
      if (result.success) {
        // Don't show alert - let the component handle UI updates
        // Refresh all data in parallel
        Promise.allSettled([
          fetchLeads(true),
          fetchStats(true),
          fetchFollowUps(true),
          window.postSalesRefresh?.(),
          window.potentialClientsRefresh?.(),
          window.activityRefresh?.(),
          window.highlightsRefresh?.(),
        ]);
        return result; // Return result so component can check success
      } else {
        // Only show error alerts
        alert(result.error || 'Failed to convert to potential client');
        return result;
      }
    } catch (error) {
      console.error('Error converting to potential client:', error);
      // Only show error alerts
      if (error.response?.data?.error) {
        alert('Error converting to potential client: ' + error.response.data.error);
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Mark follow-up as completed
  // Intentionally does NOT flip global loading — FollowUpTracker shows a full-page spinner when loading=true,
  // which used to wipe the filter bar/scroll position on every Done click. The row-level button handles its own
  // in-flight state.
  const handleMarkCompleted = async (id, followUpNumber = 1, isPostSales = false, followUpDate = null) => {
    try {
      console.log(`Marking ${isPostSales ? 'post-sales' : ''} follow-up ${followUpNumber} as completed for lead ${id}`);

      let result;
      if (isPostSales) {
        // Handle post-sales follow-up
        result = await dashboardAPI.markPostSalesFollowUpCompleted(id, followUpNumber);
      } else {
        // Handle pre-sales follow-up
        // The date this row is FOR. A lead queued from its "First Sales Message
        // Sent" date has no follow_ups record yet, so the server creates one —
        // and without the date it would be filed under today rather than the
        // day the pitch actually went out.
        result = await leadsAPI.markCompleted(id, followUpNumber, followUpDate ? { followUpDate } : {});
      }

      if (result && result.success) {
        // Silently refresh follow-up data only (no alert)
        // Await the refresh directly instead of using setTimeout to avoid
        // race conditions with loading state that could show stale data
        try {
          await fetchFollowUps(true, true); // silent: keep the follow-ups list mounted
        } catch (refreshError) {
          console.error('Error refreshing follow-ups after marking completed:', refreshError);
        }

        // Refresh other data sources without blocking or toggling loading state
        // Use Promise.allSettled so failures don't prevent other refreshes
        const refreshPromises = [];
        refreshPromises.push(fetchStats(true).catch(e => console.error('Error refreshing stats:', e)));
        refreshPromises.push(fetchLeads(true).catch(e => console.error('Error refreshing leads:', e)));

        if (window.postSalesRefresh) {
          refreshPromises.push(Promise.resolve().then(() => window.postSalesRefresh()));
        }
        if (window.potentialClientsRefresh) {
          refreshPromises.push(Promise.resolve().then(() => window.potentialClientsRefresh()));
        }
        if (window.activityRefresh) {
          refreshPromises.push(Promise.resolve().then(() => window.activityRefresh()));
        }
        if (window.highlightsRefresh) {
          refreshPromises.push(Promise.resolve().then(() => window.highlightsRefresh()));
        }

        await Promise.allSettled(refreshPromises);

        // If we're editing a lead, refresh the lead data to show updated follow-ups
        if (editingLead && editingLead.id === id) {
          try {
            const updatedLead = await leadsAPI.getById(id);
            setEditingLead(updatedLead.data);
          } catch (e) {
            console.error('Error refreshing edited lead:', e);
          }
        }
      } else {
        throw new Error(result?.error || 'Failed to mark follow-up as completed');
      }
    } catch (error) {
      console.error('Error marking follow-up as completed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error marking follow-up as completed';
      alert(errorMessage);
    }
  };

  // Handle edit lead
  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setShowModal(true);
  };

  // Open pre-sales lead form by ID (e.g. from Send Emails "view form" icon)
  const handleViewLeadFromSendEmails = async (leadId) => {
    if (!leadId) return;
    try {
      const res = await leadsAPI.getById(leadId);
      if (res?.data) {
        setEditingLead(res.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error('Failed to load lead:', err);
    }
  };

  // Handle add lead
  const handleAddLead = () => {
    setEditingLead(null);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    if (returnToPostSalesLead) {
      setActiveTab('post sales');
      setPostSalesReopenLead(returnToPostSalesLead);
      setReturnToPostSalesLead(null);
    }
    setShowModal(false);
    setEditingLead(null);
    setExtensionPrefill(null);
  };

  // When switching tabs via sidebar, close the lead modal so the selected tab content shows immediately (avoids lag / needing to refresh)
  const handleSectionChange = (newSection) => {
    if (showModal) {
      setShowModal(false);
      setEditingLead(null);
    }
    setActiveTab(newSection);
  };

  // Export data to Excel with visual summary - respects current page filters
  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Dynamically import XLSX
      const XLSX = await import('xlsx');
      
      let exportData = [];
      let fileName = '';
      let sheetName = '';
      
      // Export based on current active tab with filters
      if (activeTab === 'pre sales') {
        // Export Pre Sales leads with current filters
        const params = {
          bde: selectedBDE !== 'All' ? selectedBDE : undefined,
          search: searchTerm || undefined,
          stage: selectedStage !== 'All' ? selectedStage : undefined,
          priority: selectedPriority !== 'All' ? selectedPriority : undefined
        };
        
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (selectedDate) params.date = selectedDate;
        
        const leadsResponse = await leadsAPI.getAll(params);
        exportData = leadsResponse.data || [];
        fileName = 'pre_sales';
        sheetName = 'Pre Sales Leads';
        
        if (exportData.length === 0) {
          alert('No data to export with current filters');
        return;
      }

      } else if (activeTab === 'post sales') {
        // Export Post Sales data with current filters
        // Get filter values from PostSales component via window
        const postSalesFilters = window.postSalesFilters || {};
        const params = {
          bde: postSalesFilters.selectedBDE !== 'All' ? postSalesFilters.selectedBDE : undefined,
          search: postSalesFilters.searchTerm || undefined,
          date_from: postSalesFilters.dateFrom || undefined,
          date_to: postSalesFilters.dateTo || undefined
        };
        
        const leadsResponse = await dashboardAPI.getPostSalesLeads(params);
        exportData = leadsResponse.data || [];
        fileName = 'post_sales';
        sheetName = 'Post Sales Leads';
        
        if (exportData.length === 0) {
          alert('No data to export with current filters');
          return;
        }
        
      } else if (activeTab === 'potential clients') {
        // Export Potential Clients with current filters
        const potentialClientsFilters = window.potentialClientsFilters || {};
        const params = {
          bde: potentialClientsFilters.selectedBDE !== 'All' ? potentialClientsFilters.selectedBDE : undefined,
          search: potentialClientsFilters.searchTerm || undefined,
          date_from: potentialClientsFilters.dateFrom || undefined,
          date_to: potentialClientsFilters.dateTo || undefined
        };
        
        const clientsResponse = await dashboardAPI.getPotentialClients(params);
        exportData = clientsResponse.data || [];
        fileName = 'potential_clients';
        sheetName = 'Potential Clients';
        
        if (exportData.length === 0) {
          alert('No data to export with current filters');
          return;
        }
      } else if (activeTab === 'onboarded clients') {
        // Export Onboarded Clients with current filters
        const onboardedClientsFilters = window.onboardedClientsFilters || {};
        const params = {
          bde: onboardedClientsFilters.selectedBDE !== 'All' ? onboardedClientsFilters.selectedBDE : undefined,
          search: onboardedClientsFilters.searchTerm || undefined,
          date_from: onboardedClientsFilters.dateFrom || undefined,
          date_to: onboardedClientsFilters.dateTo || undefined
        };
        
        const clientsResponse = await dashboardAPI.getOnboardedClients(params);
        exportData = clientsResponse.data || [];
        fileName = 'onboarded_clients';
        sheetName = 'Onboarded Clients';
        
        if (exportData.length === 0) {
          alert('No data to export with current filters');
          return;
        }
      } else {
        alert('Export is only available for Pre Sales, Post Sales, Potential Clients, and Onboarded Clients');
        return;
      }

      // Map export data based on page type
      let exportDataSheet = [];
      
      if (activeTab === 'pre sales') {
        exportDataSheet = exportData.map(lead => ({
        'Lead Date': lead.lead_date || '',
        'Company Name': lead.company_name || '',
        'Core Business': lead.core_business || '',
        'City': lead.city || '',
        'Country': lead.country || '',
        'POC Name': lead.poc_name || '',
        'Designation': lead.designation || '',
        'Email': lead.email || '',
        'Contact No': lead.contact_no || '',
        'Lead Source': lead.lead_source || '',
        'Stage': lead.stage || '',
        'Priority': lead.priority || '',
        'BDE Name': lead.bde_name || '',
        'POCs Shortlisted': lead.poc_shortlisted || 0,
        'Emails Sent': lead.email_sent || 0,
        'New Connections': lead.new_connection || 0,
          'Auto Replies': lead.auto_reply_received || 0
        }));
      } else if (activeTab === 'post sales') {
        exportDataSheet = exportData.map(lead => ({
          'Company Name': lead.company_name || '',
          'Country': lead.country || '',
          'POC Name': lead.poc_name || '',
          'POC Designation': lead.poc_designation || '',
          'POC Email': lead.poc_email || '',
          'POC Contact': lead.poc_contact_no || '',
          'Company URL': lead.company_url || '',
          'LinkedIn URL': lead.linkedin_url || '',
          'BDE Name': lead.bde_name || ''
        }));
      } else if (activeTab === 'potential clients') {
        exportDataSheet = exportData.map(client => ({
          'Converted Date': client.converted_date || '',
          'Company Name': client.company_name || '',
          'Country': client.country || '',
          'City': client.city || '',
          'POC Name': client.poc_name || '',
          'POC Designation': client.poc_designation || '',
          'POC Email': client.poc_email || '',
          'POC Contact': client.poc_contact_no || '',
          'Company URL': client.company_url || '',
          'LinkedIn URL': client.linkedin_url || '',
          'Converted From': client.converted_from || '',
          'BDE Name': client.bde_name || '',
          'Stage': client.stage || '',
          'Priority': client.priority || ''
        }));
      } else if (activeTab === 'onboarded clients') {
        exportDataSheet = exportData.map(client => ({
          'Onboarding Date': client.onboarding_date || '',
          'Company Name': client.company_name || '',
          'Country': client.country || '',
          'City': client.city || '',
          'POC Name': client.poc_name || '',
          'POC Designation': client.poc_designation || '',
          'POC Email': client.poc_email || '',
          'POC Contact': client.poc_contact_no || '',
          'Company URL': client.company_url || '',
          'LinkedIn URL': client.linkedin_url || '',
          'BDE Name': client.bde_name || '',
          'Stage': client.stage || '',
          'Priority': client.priority || ''
        }));
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportDataSheet);
      
      // Set column widths
      ws['!cols'] = exportDataSheet.length > 0 
        ? Object.keys(exportDataSheet[0]).map(() => ({ wch: 20 }))
        : [];
      
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate file and download
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `${fileName}_${timestamp}.xlsx`);
      // Export completed silently
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Single consolidated effect: fetch data based on active tab and filters.
  // The fetch functions themselves deduplicate (skip if same filters and data already loaded).
  useEffect(() => {
    if (activeTab === 'pre sales' || activeTab === 'analytics') {
      fetchLeads();
    }
    if (activeTab === 'analytics') {
      fetchStats();
    }
    if (activeTab === 'follow-ups' || activeTab === 'send emails') {
      fetchFollowUps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // ↑ NOTE: filter inputs (BDE / search / stage / priority / tabFilters) no
  // longer appear in this dep list — filters are applied client-side now
  // (see the filteredLeads useMemo below). This is what makes the Pre-Sales
  // and Post-Sales pages stop "reloading" every time you tweak a filter.

  const showExportBtn = activeTab === 'pre sales' || activeTab === 'post sales' || activeTab === 'potential clients' || activeTab === 'onboarded clients';
  const handleExportPDF = () => {
    window.print();
  };

  return (
    <div className={`h-screen flex overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Mobile sidebar overlay backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar: hidden on mobile, visible on desktop. Mobile uses fixed overlay. */}
      <div className={`
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static
        fixed inset-y-0 left-0 z-50
        transition-transform duration-300 ease-in-out
      `}>
        <LeftSidebar
          activeSection={activeTab}
          onSectionChange={(section) => { handleSectionChange(section); setMobileSidebarOpen(false); }}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          darkMode={darkMode}
          user={user}
          onLogout={logout}
          onOpenChangeLogs={() => { handleSectionChange('change logs'); setMobileSidebarOpen(false); }}
          onOpenUserManagement={() => { setShowUserManagement(true); setMobileSidebarOpen(false); }}
        />
      </div>

      <div className="flex-1 flex flex-col h-screen overflow-hidden min-w-0">
        <Header
          onOpenChangeLogs={() => handleSectionChange('change logs')}
          onOpenUserManagement={() => setShowUserManagement(true)}
          onToggleMobileSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        <main className={`flex-1 min-h-0 flex flex-col ${activeTab === 'send emails' ? 'pt-0 px-3 pb-3 lg:px-6 lg:pb-6' : 'p-3 lg:p-6'} ${showModal || (showUserManagement && isAdmin()) ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {showModal ? (
            <LeadModal
              key={editingLead?.id || 'new'}
              isOpen={true}
              onClose={handleCloseModal}
              onSubmit={handleSaveLead}
              lead={editingLead}
              loading={loading}
              onCloseDeal={undefined}
              onConvertToPotentialClient={handleConvertToPotentialClient}
              inline={true}
              initialPrefill={extensionPrefill}
            />
          ) : showUserManagement && isAdmin() ? (
            <UserManagement
              isOpen={true}
              onClose={() => setShowUserManagement(false)}
              inline={true}
            />
          ) : (
            <>
        {activeTab === 'pre sales' && (
          <>
            <FilterBar
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              selectedBDE={selectedBDE}
              setSelectedBDE={setSelectedBDE}
              selectedStage={selectedStage}
              setSelectedStage={setSelectedStage}
              selectedPriority={selectedPriority}
              setSelectedPriority={setSelectedPriority}
              selectedInterest={selectedInterest}
              setSelectedInterest={setSelectedInterest}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              countryOptions={countryOptions}
              selectedLeadSource={selectedLeadSource}
              setSelectedLeadSource={setSelectedLeadSource}
              leadSourceOptions={leadSourceOptions}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              showDateRange={true}
              showSearch={true}
              showStageFilter={true}
              showPriorityFilter={true}
              showInterestFilter={true}
              showBDE={true}
              onExportExcel={handleExport}
              onExportPDF={handleExportPDF}
              showExport={showExportBtn}
            />
            <LeadsTable
              leads={filteredLeads}
              loading={loading}
              sortOrder={leadSortOrder}
              onToggleSort={(next) => {
                // Accept either an explicit mode from the <select>, or toggle desc/asc when called
                // with no argument (keeps any legacy callers working).
                setLeadSortOrder(prev => {
                  if (typeof next === 'string') return next;
                  return prev === 'desc' ? 'asc' : 'desc';
                });
              }}
              onAddLead={handleAddLead}
              onEditLead={handleEditLead}
              onDeleteLead={handleDeleteLead}
              onCloseDeal={undefined} // Close Deal only available from Potential Clients tab, not Pre Sales
            />
          </>
        )}

        {activeTab === 'activity' && (
          <DailyActivityTracker
            selectedDate={selectedDate}
            selectedBDE={selectedBDE}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={false}
          />
        )}

        {activeTab === 'highlights' && (
          <Highlights
            onRefreshReady={(refreshFn) => {
              window.highlightsRefresh = refreshFn;
            }}
            onNavigateToPreSales={(lead) => handleEditLead(lead)}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={false}
          />
        )}

        {activeTab === 'post sales' && (
          <>
            <FilterBar
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
              selectedBDE={selectedBDE}
              setSelectedBDE={setSelectedBDE}
              selectedStage={selectedStage}
              setSelectedStage={setSelectedStage}
              selectedPriority={selectedPriority}
              setSelectedPriority={setSelectedPriority}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              showDateRange={true}
              showSearch={true}
              showStageFilter={true}
              showPriorityFilter={true}
              showBDE={true}
              onExportExcel={handleExport}
              onExportPDF={handleExportPDF}
              showExport={showExportBtn}
              stageOptions={[
                { value: 'New', label: 'New' },
                { value: 'Contacted', label: 'Contacted' },
                { value: 'Follow-up 1', label: 'Follow-up 1' },
                { value: 'Follow-up 2', label: 'Follow-up 2' },
                { value: 'Proposal', label: 'Proposal' },
                { value: 'Negotiation', label: 'Negotiation' },
                { value: 'Onboarded', label: 'Onboarded' },
                { value: 'Important - But Contact Later', label: 'Important - Contact Later' },
                { value: 'Deferred', label: 'Deferred' },
                { value: 'done', label: 'Done (Post-Sales)' },
                { value: 'in_progress', label: 'In Progress (Post-Sales)' },
                { value: 'followup', label: 'Follow-up (Post-Sales)' },
                { value: 'pending', label: 'Pending (Post-Sales)' }
              ]}
            />
            <PostSales 
            selectedBDE={selectedBDE}
            selectedStage={selectedStage}
            selectedPriority={selectedPriority}
            selectedDate={selectedDate}
            dateFrom={dateFrom}
            dateTo={dateTo}
            searchTerm={searchTerm}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={showExportBtn}
            onConvertToPotentialClient={async (lead_id) => {
              try {
                console.log('Calling convertPostSalesToPotentialClient with:', { lead_id, userId: user?.id });
                const result = await dashboardAPI.convertPostSalesToPotentialClient(lead_id, user?.id);
                console.log('Result from API:', result);
                if (result.success) {
                  // No alert, just refresh
                  fetchStats(true);
                  return result;
                } else {
                  console.error('API returned error:', result);
                  throw new Error(result.error || result.message || 'Failed to convert to potential client');
                }
              } catch (error) {
                console.error('Error in onConvertToPotentialClient:', error);
                console.error('Error response:', error.response);
                throw error;
              }
            }}
            onNavigateToPreSales={(lead) => {
              setReturnToPostSalesLead(lead);
              handleEditLead(lead);
            }}
            reopenLead={postSalesReopenLead}
            onClearReopenLead={() => setPostSalesReopenLead(null)}
          />
          </>
        )}

        {activeTab === 'onboarded clients' && (
          <OnboardedClients 
            key={activeTab} // Force re-render when tab becomes active
            onNavigateToPreSales={(lead) => {
              // Open Pre Sales form without switching tabs
              handleEditLead(lead);
            }}
          />
        )}

        {activeTab === 'pipeline clients' && (

          <PipelineClients

            onNavigateToPreSales={(lead) => handleEditLead(lead)}

            onExportExcel={handleExport}

            onExportPDF={handleExportPDF}

            showExport={false}

          />

        )}


        {activeTab === 'meetings' && (

          <Meetings

            leads={leads}

            onNavigateToPreSales={handleViewLeadFromSendEmails}

          />

        )}


        {activeTab === 'potential clients' && (
          <PotentialClients 
            onNavigateToPreSales={(lead) => {
              handleEditLead(lead);
            }}
            onCloseDeal={undefined}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={showExportBtn}
          />
        )}

        {activeTab === 'follow-ups' && (
          <FollowUpTracker
            followUps={followUps}
            loading={loading}
            onMarkCompleted={handleMarkCompleted}
            onNavigateToSendEmails={() => setActiveTab('send emails')}
            onNavigateToPreSales={(lead) => handleEditLead(lead)}
            onNavigateToPostSales={(lead) => {
              setActiveTab('post sales');
              setPostSalesReopenLead(lead);
            }}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={false}
          />
        )}

        {/* Keep SendEmails mounted when dashboard is visible so added rows persist until user clicks Done (tab switch / refresh won't clear them) */}
        <div style={{ display: activeTab === 'send emails' ? 'block' : 'none' }}>
          <SendEmails
            followUps={followUps}
            loading={loading}
            onRefresh={fetchFollowUps}
            onMarkCompleted={handleMarkCompleted}
            onViewLead={handleViewLeadFromSendEmails}
            onExportExcel={handleExport}
            onExportPDF={handleExportPDF}
            showExport={showExportBtn}
          />
        </div>

        {activeTab === 'templates' && <EmailTemplates />}

        {activeTab === 'email log' && (
          <EmailLog onViewLead={handleViewLeadFromSendEmails} />
        )}

        {activeTab === 'ai assistant' && (
          <AiAssistant
            onViewLead={(id) => {
              setActiveTab('pre sales');
              handleViewLeadFromSendEmails(id);
            }}
          />
        )}

        {activeTab === 'change logs' && (
          <ChangeLogs />
        )}

        {activeTab === 'analytics' && (
          <Analytics
            leads={leads}
            stats={stats}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            selectedBDE={selectedBDE}
            setSelectedBDE={setSelectedBDE}
            loading={loading}
            onRefresh={fetchStats}
          />
        )}

        {activeTab === 'lead gen agent' && (
          <LeadGenAgent />
        )}

        {activeTab === 'leads' && (
          <StagingLeads />
        )}
            </>
          )}
        </main>
      </div>

      {/* Virtual Sales Agent — floating chat widget */}
      <VirtualSalesAgent />
    </div>
  );
};

// Login Screen Component (shown when not logged in)
const LoginScreen = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  // When the API rejects an expired session it reloads the app and leaves the
  // reason here, so the person sees why they are looking at the login screen.
  // Read without removing: React may run this initializer twice (StrictMode in
  // development), and removing here meant the second run found nothing — so
  // "everyone has been signed out" never reached the screen. Cleared once shown.
  const [error, setError] = useState(() => {
    try { return sessionStorage.getItem('authMessage') || ''; } catch { return ''; }
  });
  useEffect(() => {
    try { sessionStorage.removeItem('authMessage'); } catch { /* ignore */ }
  }, []);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: code, 3: new password
  const [forgotPasswordData, setForgotPasswordData] = useState({ email: '', code: '', newPassword: '', confirmPassword: '' });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password);
      
      console.log('Login result:', result);
      
      if (result.success && result.user) {
        // Login successful, user will be redirected automatically
        console.log('✅ Login successful, user:', result.user);
        setFormData({ email: '', password: '' });
      } else {
        // Login failed - keep form data and show error
        console.log('❌ Login failed:', result.error);
        setError(result.error || 'Invalid email or password');
        // Don't clear form on error - keep email/password so user can try again
      }
    } catch (error) {
      console.error('Login exception:', error);
      setError('Login failed. Please try again.');
      // Don't clear form on error
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);
    setForgotPasswordLoading(true);

    if (forgotPasswordStep === 1) {
      // Step 1: Request code
      if (!forgotPasswordData.email) {
        setForgotPasswordError('Please enter your email');
        setForgotPasswordLoading(false);
        return;
      }

      try {
        const result = await dashboardAPI.forgotPassword(forgotPasswordData.email);
        
        if (result.success && result.codeSent) {
          setForgotPasswordSuccess(true);
          setForgotPasswordStep(2); // Move to code verification step
        } else {
          setForgotPasswordError(result.error || 'Failed to send reset code. Please check your email configuration or contact your administrator.');
        }
      } catch (error) {
        setForgotPasswordError('An error occurred. Please try again or contact your administrator.');
      }
    } else if (forgotPasswordStep === 2) {
      // Step 2: Verify code
      if (!forgotPasswordData.code) {
        setForgotPasswordError('Please enter the code');
        setForgotPasswordLoading(false);
        return;
      }

      try {
        const result = await dashboardAPI.verifyResetCode(forgotPasswordData.email, forgotPasswordData.code);
        
        if (result.success) {
          setForgotPasswordSuccess(true);
          setForgotPasswordStep(3); // Move to password reset step
        } else {
          setForgotPasswordError(result.error || 'Invalid or expired code. Please request a new code.');
        }
      } catch (error) {
        setForgotPasswordError('An error occurred. Please try again.');
      }
    } else if (forgotPasswordStep === 3) {
      // Step 3: Reset password
      if (!forgotPasswordData.newPassword || !forgotPasswordData.confirmPassword) {
        setForgotPasswordError('Please enter and confirm your new password');
        setForgotPasswordLoading(false);
        return;
      }

      if (forgotPasswordData.newPassword.length < 10) {
        setForgotPasswordError('Password must be at least 10 characters, with a letter and a number long');
        setForgotPasswordLoading(false);
        return;
      }

      if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
        setForgotPasswordError('Passwords do not match');
        setForgotPasswordLoading(false);
        return;
      }

      try {
        const result = await dashboardAPI.resetPassword(
          forgotPasswordData.email, 
          forgotPasswordData.code, 
          forgotPasswordData.newPassword
        );
        
        if (result.success) {
          setForgotPasswordSuccess(true);
          // Reset form and close modal after 2 seconds
          setTimeout(() => {
            setShowForgotPassword(false);
            setForgotPasswordStep(1);
            setForgotPasswordData({ email: '', code: '', newPassword: '', confirmPassword: '' });
            setForgotPasswordError('');
            setForgotPasswordSuccess(false);
          }, 2000);
        } else {
          setForgotPasswordError(result.error || 'Failed to reset password. Please try again.');
        }
      } catch (error) {
        setForgotPasswordError('An error occurred. Please try again.');
      }
    }

    setForgotPasswordLoading(false);
  };

  const handleForgotPasswordChange = (e) => {
    setForgotPasswordData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setForgotPasswordError('');
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Main Login Card - Clean white card with shadow */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header with Logo and Title */}
          <div className="px-8 pt-2 pb-1">
            {/* Centered Logo */}
            <div className="flex justify-center -mb-4">
              <img 
                src="/logo.jpg" 
                alt="Logo" 
                className="w-72 h-72 object-contain"
                style={{ maxWidth: '320px', maxHeight: '320px' }}
                onError={(e) => {
                  // Fallback if logo doesn't exist
                  e.target.style.display = 'none';
                }}
              />
                </div>
            
            {/* Centered Title */}
            <div className="text-center -mt-4 mb-0">
              <h1 className="text-3xl font-bold">
                <span className="text-black">Sales</span> <span className="text-[#679632]">Hub</span>
              </h1>
            </div>
            <p className="text-xs text-gray-600 uppercase tracking-wide text-center mt-0">SIGN IN TO CONTINUE</p>
          </div>
          
          {/* Login Form Section */}
          <div className="px-8 pb-8">
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all text-sm"
                      placeholder="Enter your email"
                      required
                    />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all text-sm"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#679632] hover:text-[#587f2b] font-medium"
                  >
                    Forgot Password?
                  </button>
                </div>

                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 text-red-700 text-sm p-3 rounded">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#679632] text-white py-3 px-4 rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-base transition-colors"
                >
                  {loading ? 'Logging in...' : 'Sign In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Reset Password</h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordStep(1);
                  setForgotPasswordData({ email: '', code: '', newPassword: '', confirmPassword: '' });
                  setForgotPasswordError('');
                  setForgotPasswordSuccess(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
              </button>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              {/* Step 1: Enter Email */}
              {forgotPasswordStep === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={forgotPasswordData.email}
                      onChange={handleForgotPasswordChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    We'll send a 6-digit code to your email address.
                  </p>
                </>
              )}

              {/* Step 2: Enter Code */}
              {forgotPasswordStep === 2 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={forgotPasswordData.code}
                      onChange={handleForgotPasswordChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength="6"
                      required
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Enter the 6-digit code sent to your email. The code expires in 15 minutes.
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      setForgotPasswordError('');
                      setForgotPasswordLoading(true);
                      try {
                        const result = await dashboardAPI.forgotPassword(forgotPasswordData.email);
                        if (result.success) {
                          setForgotPasswordSuccess(true);
                          setTimeout(() => setForgotPasswordSuccess(false), 3000);
                        }
                      } catch (error) {
                        setForgotPasswordError('Failed to resend code. Please try again.');
                      } finally {
                        setForgotPasswordLoading(false);
                      }
                    }}
                    className="text-sm text-[#679632] hover:text-[#587f2b] font-medium"
                  >
                    Resend Code
                  </button>
                    </>
                  )}

              {/* Step 3: Enter New Password */}
              {forgotPasswordStep === 3 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={forgotPasswordData.newPassword}
                      onChange={handleForgotPasswordChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all"
                      placeholder="Enter new password (min 6 characters)"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={forgotPasswordData.confirmPassword}
                      onChange={handleForgotPasswordChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#679632] focus:border-[#679632] transition-all"
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                </>
              )}

              {forgotPasswordSuccess && forgotPasswordStep !== 3 && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 text-sm p-3 rounded">
                  {forgotPasswordStep === 1 
                    ? 'Code sent! Please check your email.'
                    : 'Code verified! Please set your new password.'}
                </div>
              )}

              {forgotPasswordSuccess && forgotPasswordStep === 3 && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-700 text-sm p-3 rounded">
                  Password reset successfully! You can now login with your new password.
                </div>
              )}

              {forgotPasswordError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 text-sm p-3 rounded">
                  {forgotPasswordError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (forgotPasswordStep > 1) {
                      setForgotPasswordStep(forgotPasswordStep - 1);
                      setForgotPasswordError('');
                      setForgotPasswordSuccess(false);
                    } else {
                      setShowForgotPassword(false);
                      setForgotPasswordStep(1);
                      setForgotPasswordData({ email: '', code: '', newPassword: '', confirmPassword: '' });
                      setForgotPasswordError('');
                      setForgotPasswordSuccess(false);
                    }
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {forgotPasswordStep > 1 ? 'Back' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="flex-1 bg-[#679632] text-white py-2 px-4 rounded-lg hover:bg-[#587f2b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {forgotPasswordLoading 
                    ? 'Processing...' 
                    : forgotPasswordStep === 1 
                      ? 'Send Code' 
                      : forgotPasswordStep === 2 
                        ? 'Verify Code' 
                        : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Main App Component with Authentication Protection
const App = () => {
  const { user, loading } = useAuth();
  const pathname = window.location.pathname;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#679632]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Suite SSO: check for suite_token in URL (can arrive at any path)
  const searchParams = new URLSearchParams(window.location.search);
  const suiteToken = searchParams.get('suite_token');

  // Suite SSO entry: /auth/suite?suite_token=...&login_hint=...
  if (pathname === '/auth/suite') {
    return <SuiteAuthPage />;
  }

  // If Suite sent user to any path with token, redirect to /auth/suite to process it
  // Always redirect if suite_token is present (even if user exists - Suite might be switching users)
  if (suiteToken && pathname !== '/auth/suite') {
    window.location.replace('/auth/suite' + window.location.search);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#679632]"></div>
          <p className="mt-4 text-gray-600">Signing you in from Suite...</p>
        </div>
      </div>
    );
  }

  if (user && user.must_change_password) return <ChangePasswordScreen />;
  return user ? <AppContent /> : <LoginScreen />;
};

// App with AuthProvider wrapper
const AppWithAuth = () => {
  return (
    <AuthProvider>
      {/* Above everything, including the login screen: a tab that has been open
          since before a deploy is still building emails with the old code. */}
      <NewVersionBanner />
      <App />
    </AuthProvider>
  );
};

export default AppWithAuth;